import React from 'react';
import { NintendoQuestSummary } from '@/lib/nintendo-quest';
import { useNintendoQuestTheme } from '../contexts/NintendoQuestThemeContext';
import ThemeSelector from '../components/ThemeSelector';
import { LegoTrajectoryCard } from '../LegoTrajectoryCard';
import LegoTimeline from '../components/LegoTimeline';

interface LegoClassicThemeProps {
  data: NintendoQuestSummary;
}

const LegoClassicTheme: React.FC<LegoClassicThemeProps> = ({ data }) => {
  const { getVocabulary } = useNintendoQuestTheme();

  // Get current week date range
  const getCurrentWeekRange = () => {
    const now = new Date();
    const currentDate = new Date(now);
    const dayOfWeek = currentDate.getDay();
    const diff = currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(currentDate.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return `${formatDate(monday)} - ${formatDate(sunday)}, ${now.getFullYear()}`;
  };

  // LEGO-specific data transformations
  const brickInventory = {
    todaysBricks: data.todaysStats.pointsEarned,
    totalBricks: data.overallProgress.currentPoints,
    builderCoins: Math.round(data.overallProgress.moneyEquivalent.total),
    bricksNeeded: data.overallProgress.remainingPoints
  };

  const buildingProgress = {
    percentage: data.overallProgress.progressPercentage,
    current: data.overallProgress.currentPoints,
    target: data.overallProgress.targetPoints
  };

  // Enhanced next challenges with REAL activity point values
  const getNextChallenges = () => {
    const challengeMap: { [key: string]: { emoji: string; title: string; description: string; points: number; difficulty: string } } = {
      'reading': { 
        emoji: '📚', 
        title: 'Build Knowledge Blocks', 
        description: '15 mins reading = building materials',
        points: 3, // Short article/story = 3 points
        difficulty: 'Easy'
      },
      'math-ixl': { 
        emoji: '🧮', 
        title: 'Calculate Building Materials', 
        description: 'IXL math problems = precise measurements',
        points: 6, // 3 exercises at 90-99% = 6 points
        difficulty: 'Easy'
      },
      'sanskrit': { 
        emoji: '🕉️', 
        title: 'Ancient Builder Wisdom', 
        description: 'Shloka study = master builder techniques',
        points: 3, // New shloka = 3 points
        difficulty: 'Medium'
      },
      'yoga': { 
        emoji: '🧘', 
        title: 'Builder Strength Training', 
        description: 'Yoga practice = strong building foundation',
        points: 1, // Daily yoga = 1 point
        difficulty: 'Easy'
      },
      'gujarati': { 
        emoji: '🗣️', 
        title: 'Master Builder Communication', 
        description: 'Language practice = team coordination',
        points: 5, // Gujarati speaking day = 5 points
        difficulty: 'Medium'
      },
      'teaching': { 
        emoji: '👨‍🏫', 
        title: 'Master Builder Teaching', 
        description: 'Teach sister = leadership skills',
        points: 3, // Teaching academic = 3 points
        difficulty: 'Medium'
      },
      'perfect-day': { 
        emoji: '⭐', 
        title: 'Master Builder Day', 
        description: 'Complete perfect day routine',
        points: 5, // Perfect day bonus = 5 points
        difficulty: 'Hard'
      },
      'math-tables': { 
        emoji: '📝', 
        title: 'Precision Engineering', 
        description: 'Math tables = exact calculations',
        points: 8, // 20 tables = 8 base points + bonus
        difficulty: 'Medium'
      }
    };

    return Object.entries(challengeMap).map(([key, challenge]) => ({
      ...challenge,
      id: key
    }));
  };

  // Weekly building activity data processing
  const weeklyBuildingData = data.currentWeek?.dailyBreakdown || [];
  const processWeeklyData = () => {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    return dayNames.map((dayName, index) => {
      const dayData = weeklyBuildingData[index] || { points: 0 };
      const points = dayData.points || 0;
      
      let blockColor = 'bg-gray-300'; // Empty day
      let blockHeight = 'h-6'; // Minimal height for empty days
      let blockLabel = 'No Building';
      
      if (points > 0) {
        // Scale the height more dramatically - max out around 60-80 points for full height
        const heightScale = Math.min(points / 60, 1); // Scale to 60 points = full height
        const baseHeight = 8; // Starting height
        const maxHeight = 32; // Maximum height (h-32)
        const calculatedHeight = Math.round(baseHeight + (heightScale * (maxHeight - baseHeight)));
        
        if (points >= 80) {
          blockColor = 'bg-blue-500';
          blockHeight = 'h-32';
          blockLabel = 'Master Building!';
        } else if (points >= 50) {
          blockColor = 'bg-green-500';
          blockHeight = `h-${Math.min(calculatedHeight, 28)}`; // h-28 max for green
          blockLabel = 'Great Building';
        } else if (points >= 25) {
          blockColor = 'bg-yellow-500';
          blockHeight = `h-${Math.min(calculatedHeight, 20)}`; // h-20 max for yellow
          blockLabel = 'Good Building';
        } else {
          blockColor = 'bg-red-500';
          blockHeight = `h-${Math.min(calculatedHeight, 12)}`; // h-12 max for red
          blockLabel = 'Light Building';
        }
      }

      return {
        day: dayName,
        points,
        blockColor,
        blockHeight,
        blockLabel
      };
    });
  };

  const weeklyData = processWeeklyData();
  
  return (
    <div 
      className="min-h-screen" 
      style={{
        background: 'linear-gradient(135deg, #ff4757, #ffa502, #2ed573, #3742fa)',
        fontFamily: "'Comic Sans MS', cursive"
      }}
    >
      {/* Constrained width container for better layout */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        
        {/* Enhanced Theme Selector with better LEGO styling */}
        <div className="flex justify-end mb-6">
          <div className="theme-selector-lego-improved">
            <ThemeSelector />
          </div>
        </div>

        {/* Enhanced Master Builder Header with Ghibli-style character */}
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            {/* Ghibli-style LEGO character image */}
            <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-white shadow-xl animate-bounce">
              <img 
                src="/rachit-lego-gh.jpg"
                alt="Master Builder Rachit"
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to emoji if image fails to load
                  console.log('Image failed to load');
                  e.currentTarget.style.display = 'none';
                  const fallbackDiv = e.currentTarget.nextElementSibling;
                  if (fallbackDiv && fallbackDiv instanceof HTMLElement) {
                    fallbackDiv.style.display = 'block';
                  }
                }}
              />
              <div className="w-full h-full flex items-center justify-center text-4xl md:text-5xl" style={{display: 'none'}}>
                👷‍♂️
              </div>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 text-shadow-lg">
            Master Builder Rachit
          </h1>
          <p className="text-xl md:text-2xl text-white font-semibold mb-4">
            Building the Ultimate Nintendo Switch Set!
          </p>
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-6 py-3 inline-block border-4 border-blue-600">
            <p className="text-blue-800 font-bold text-lg">
              🎮 Nintendo Switch Constructor Level: {Math.floor(buildingProgress.percentage / 10) + 1}
            </p>
          </div>
        </div>

        {/* Enhanced LEGO Brick Inventory Grid - Tighter Layout */}
        <div className="grid grid-cols-3 lg:grid-cols-3 gap-3 mb-8 max-w-3xl mx-auto">
          {/* Today's Bricks - Red */}
          <div className="lego-brick bg-red-500 text-white rounded-xl p-6 shadow-xl border-4 border-white relative overflow-hidden">
            <div className="lego-studs">
              <div className="stud"></div>
              <div className="stud"></div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">{brickInventory.todaysBricks}</div>
              <div className="text-sm font-semibold">Today's Bricks</div>
            </div>
          </div>

          {/* Total Bricks - Yellow */}
          <div className="lego-brick bg-yellow-500 text-white rounded-xl p-6 shadow-xl border-4 border-white relative overflow-hidden">
            <div className="lego-studs">
              <div className="stud"></div>
              <div className="stud"></div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">{brickInventory.totalBricks}</div>
              <div className="text-sm font-semibold">Total Bricks</div>
            </div>
          </div>


          {/* Bricks Needed - Blue */}
          <div className="lego-brick bg-blue-500 text-white rounded-xl p-6 shadow-xl border-4 border-white relative overflow-hidden">
            <div className="lego-studs">
              <div className="stud"></div>
              <div className="stud"></div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">{brickInventory.bricksNeeded}</div>
              <div className="text-sm font-semibold">Bricks Needed</div>
            </div>
          </div>
        </div>

        {/* Enhanced Nintendo Switch LEGO Set Construction */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 mb-8 border-4 border-blue-600 max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-blue-800 mb-2">
              🎮 Nintendo Switch LEGO Set Construction
            </h2>
            <div className="text-4xl mb-4">🕹️</div>
            <div className="text-3xl font-bold text-green-600 mb-2">
              {buildingProgress.percentage.toFixed(1)}% Complete!
            </div>
            <p className="text-blue-700 text-lg font-semibold">
              {brickInventory.bricksNeeded} more bricks to complete your Nintendo Switch set!
            </p>
          </div>
          
          {/* Enhanced Progress Visualization */}
          <div className="relative bg-gray-200 rounded-lg h-24 overflow-hidden border-4 border-gray-400">
            <div 
              className="h-full bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 to-blue-500 transition-all duration-1000 ease-out flex items-center justify-center"
              style={{ width: `${buildingProgress.percentage}%` }}
            >
              <span className="text-white font-bold text-lg drop-shadow-lg">
                Building Progress
              </span>
            </div>
            
            {/* Progress milestones */}
            <div className="absolute top-0 left-1/4 w-0.5 h-full bg-white/60"></div>
            <div className="absolute top-0 left-1/2 w-0.5 h-full bg-white/60"></div>
            <div className="absolute top-0 left-3/4 w-0.5 h-full bg-white/60"></div>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-blue-800 font-semibold">
              Progress: {buildingProgress.current} / {buildingProgress.target} bricks
            </p>
          </div>
        </div>

        <LegoTimeline 
          allWeeks={data.allWeeks} 
          currentWeekIndex={data.currentWeekIndex}
          onWeekChange={(weekIndex) => {
            console.log(`Week ${weekIndex + 1} selected`);
          }}
        />

        <LegoTrajectoryCard 
              currentPoints={data.overallProgress.currentPoints}
              targetPoints={data.overallProgress.targetPoints}
              dailyAverage={data.overallProgress.averagePointsPerDay}
              targetDate={data.settings.questEndDate} // "2025-08-10"
              daysActive={Math.ceil((new Date().getTime() - new Date(data.settings.questStartDate).getTime()) / (1000 * 60 * 60 * 24))}
            />

        {/* Enhanced Next Building Challenges with Activity Mapping */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 border-4 border-yellow-600 max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-yellow-800 mb-2">
              🎯 Next Building Challenges
            </h2>
            <p className="text-yellow-700 font-semibold">
              Complete these activities to earn more building materials!
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getNextChallenges().slice(0, 6).map((challenge, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-yellow-100 to-orange-100 rounded-lg p-4 border-3 border-yellow-400 hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-start gap-3">
                  <div className="text-3xl flex-shrink-0">{challenge.emoji}</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-yellow-800 text-lg mb-1">
                      {challenge.title}
                    </h3>
                    <p className="text-yellow-700 text-sm mb-2">
                      {challenge.description}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        +{challenge.points} bricks
                      </span>
                      <span className="text-yellow-600 text-xs font-semibold">
                        {challenge.difficulty}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-yellow-800 font-semibold text-lg">
              Each activity you complete adds bricks to your Nintendo Switch set! 🎮
            </p>
          </div>
        </div>

        {/* Footer message */}
        <div className="text-center mt-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-6 py-4 inline-block border-4 border-purple-600">
            <p className="text-purple-800 font-bold text-xl">
              Keep building, Master Builder Rachit! Your Nintendo Switch awaits! 🧱✨
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced LEGO Brick CSS Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        /* Enhanced Theme Selector for LEGO - Cleaner Design */
        .theme-selector-lego-improved .absolute div {
          background: rgba(255, 255, 255, 0.98) !important;
          border: 2px solid #ff4757 !important;
          border-radius: 12px !important;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }

        .theme-selector-lego-improved .absolute button {
          color: #1e293b !important; /* Dark text for light background */
        }

        .theme-selector-lego-improved .absolute button:hover {
          background: #fef2f2 !important;
          color: #1e293b !important;
        }

        .theme-selector-lego-improved .relative button {
          background: rgba(255, 255, 255, 0.98) !important;
          color: #1e293b !important;
          border: 3px solid #ff4757 !important;
          font-weight: bold;
          border-radius: 12px !important;
          box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
        }
        
        .theme-selector-lego-improved .relative button:hover {
          background: rgba(255, 255, 255, 1) !important;
          border-color: #ff3742 !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(255, 71, 87, 0.4);
        }
        
        .theme-selector-lego-improved .absolute div {
          background: rgba(255, 255, 255, 0.98) !important;
          border: 2px solid #ff4757 !important;
          border-radius: 12px !important;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }
        
        .theme-selector-lego-improved .absolute button:hover {
          background: #fef2f2 !important;
        }
        
        /* Text shadow for better visibility */
        .text-shadow-lg {
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        /* LEGO Brick Effects */
        .lego-brick {
          position: relative;
          transition: all 0.3s ease;
        }
        
        .lego-brick:hover {
          transform: translateY(-8px) scale(1.05);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }
        
        .lego-studs {
          position: absolute;
          top: 8px;
          left: 50%;
          transform: translateX(-50%);
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          z-index: 10;
        }
        
        .stud {
          width: 12px;
          height: 12px;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 50%;
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .lego-brick:hover .stud {
          background: rgba(255, 255, 255, 0.4);
          transform: scale(1.05);
          transition: all 0.3s ease;
        }
        
        /* Builder Bounce Animation */
        @keyframes builder-bounce {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        
        .animate-bounce {
          animation: builder-bounce 2s infinite;
        }
        
        /* Weekly Progress Bar Animation */
        @keyframes week-bar-grow {
          from { height: 20px; }
          to { height: var(--target-height); }
        }
        
        .week-bar {
          animation: week-bar-grow 1s ease-out;
        }
        
        /* Responsive improvements */
        @media (max-width: 768px) {
          .lego-studs {
            gap: 6px;
          }
          
          .stud {
            width: 10px;
            height: 10px;
          }
          
          /* Mobile weekly blocks */
          .week-bar {
            min-width: 40px;
          }
          
          /* Mobile-friendly header */
          h1 {
            font-size: 2rem !important;
          }
          
          /* Better mobile spacing */
          .max-w-4xl, .max-w-3xl, .max-w-6xl {
            margin-left: 1rem;
            margin-right: 1rem;
          }
        }
      `}} />
    </div>
  );
};

export default LegoClassicTheme;